import { useState, useEffect } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { api } from '../api/axios'

interface ResultadoItem {
  posicao: number
  nome_atirador: string
  clube_nome: string
  categoria: string
  total: number | null
  qtd_x: number | null
  // Precisao
  total_pontos: number | null
  // Silhueta
  carneiro: number | null
  peru: number | null
  porco: number | null
  galinha: number | null
}

interface EtapaResultado {
  idetapa: number
  numero_etapa: number
  titulo: string
  titulo_reduzido: string
  resultados: ResultadoItem[]
}

interface ModalidadeResultado {
  idmodalidade: number
  nome: string
  grupo: string
  etapas: EtapaResultado[]
}

interface ResultadosResponse {
  modalidades: ModalidadeResultado[]
}

interface Campeonato {
  idcampeonato: number
  descricao: string
  temporada: number
  clube_nome: string
}

interface RankingItem {
  posicao: number
  idatirador: number
  nome_atirador: string
  clube_nome: string
  etapas: Record<number, number>
  total_geral: number
}

interface RankingGeralResponse {
  grupo: string
  etapas_disponiveis: Array<{ idetapa: number; numero_etapa: number; titulo_reduzido: string; titulo: string }>
  ranking: RankingItem[]
}

type FiltroEtapa = 'todas' | number // number = idetapa

export function ResultadosPublicoPage() {
  const [searchParams] = useSearchParams()
  const [campeonatos, setCampeonatos] = useState<Campeonato[]>([])
  const [idcampeonato, setIdcampeonato] = useState<number | null>(null)
  const [data, setData] = useState<ResultadosResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [tabModalidade, setTabModalidade] = useState<number | null>(null)
  const [filtroEtapa, setFiltroEtapa] = useState<FiltroEtapa | null>(null)

  // Ranking geral state
  const [rankingGeral, setRankingGeral] = useState<RankingGeralResponse | null>(null)
  const [loadingRanking, setLoadingRanking] = useState(false)

  useEffect(() => {
    api.get<{ data: Campeonato[] }>('/publico/campeonatos')
      .then(r => {
        const camps = r.data.data
        setCampeonatos(camps)
        const paramId = searchParams.get('idcampeonato')
        if (paramId) {
          setIdcampeonato(parseInt(paramId, 10))
        } else if (camps.length > 0) {
          setIdcampeonato(camps[0].idcampeonato)
        }
      })
      .catch(() => setError('Erro ao carregar campeonatos.'))
  }, [searchParams])

  useEffect(() => {
    if (!idcampeonato) return
    setLoading(true)
    setError(null)
    setTabModalidade(null)
    setFiltroEtapa(null)
    setRankingGeral(null)
    api.get<ResultadosResponse>(`/publico/resultados?idcampeonato=${idcampeonato}`)
      .then(r => {
        setData(r.data)
        if (r.data.modalidades.length > 0) {
          const first = r.data.modalidades[0]
          setTabModalidade(first.idmodalidade)
          // Selecionar por padrao a etapa mais recente (maior numero_etapa)
          if (first.etapas.length > 0) {
            const etapasOrdenadas = [...first.etapas].sort((a, b) => b.numero_etapa - a.numero_etapa)
            setFiltroEtapa(etapasOrdenadas[0].idetapa)
          }
        }
      })
      .catch(() => setError('Erro ao carregar resultados. Tente novamente.'))
      .finally(() => setLoading(false))
  }, [idcampeonato])

  // Buscar ranking geral quando filtro = 'todas'
  useEffect(() => {
    if (filtroEtapa !== 'todas' || !idcampeonato || !tabModalidade) return
    setLoadingRanking(true)
    setRankingGeral(null)
    api.get<RankingGeralResponse>(`/publico/ranking-geral?idcampeonato=${idcampeonato}&idmodalidade=${tabModalidade}`)
      .then(r => setRankingGeral(r.data))
      .catch(() => setError('Erro ao carregar ranking geral.'))
      .finally(() => setLoadingRanking(false))
  }, [filtroEtapa, idcampeonato, tabModalidade])

  const campNome = campeonatos.find(c => c.idcampeonato === idcampeonato)?.descricao ?? ''
  const modalidadeSel = data?.modalidades.find(m => m.idmodalidade === tabModalidade)

  function handleSelectModalidade(id: number) {
    setTabModalidade(id)
    setRankingGeral(null)
    const mod = data?.modalidades.find(m => m.idmodalidade === id)
    if (mod && mod.etapas.length > 0) {
      const etapasOrdenadas = [...mod.etapas].sort((a, b) => b.numero_etapa - a.numero_etapa)
      setFiltroEtapa(etapasOrdenadas[0].idetapa)
    } else {
      setFiltroEtapa(null)
    }
  }

  function handleFiltroEtapa(valor: FiltroEtapa) {
    setFiltroEtapa(valor)
    if (valor !== 'todas') setRankingGeral(null)
  }

  const etapaSel = filtroEtapa !== 'todas'
    ? modalidadeSel?.etapas.find(e => e.idetapa === filtroEtapa)
    : null

  const totalComResultados = data?.modalidades.reduce(
    (acc, m) => acc + m.etapas.reduce((s, e) => s + e.resultados.length, 0), 0,
  ) ?? 0

  // Monta opcoes do select de etapa para a modalidade selecionada
  const etapasDisponiveis = modalidadeSel?.etapas ?? []
  const etapasOrdenadasDesc = [...etapasDisponiveis].sort((a, b) => b.numero_etapa - a.numero_etapa)

  return (
    <div className="min-h-screen flex flex-col" style={{ background: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a2e 40%, #16213e 70%, #0f3460 100%)' }}>
      {/* Header */}
      <header className="border-b border-white/10 px-6 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3">
          <img
            src="/sponsors/logo-aguia.jpeg"
            alt="Aguia"
            className="w-10 h-10 rounded-full object-cover border-2 border-yellow-500"
            onError={e => { (e.target as HTMLImageElement).style.display = 'none' }}
          />
          <div>
            <p className="text-white font-bold text-sm">Campeonato 2026 Aguia</p>
            <p className="text-compumaq-textoSuave text-xs">1&ordf; e 2&ordf; Etapas</p>
          </div>
        </Link>
        <Link to="/" className="text-sm text-compumaq-textoSuave hover:text-white border border-white/20 px-3 py-1.5 rounded-lg transition-colors">
          &larr; Voltar
        </Link>
      </header>

      {/* Content */}
      <div className="flex-1 px-4 py-8 max-w-4xl mx-auto w-full">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-black text-white mb-2">Resultados</h1>
          <p className="text-compumaq-textoSuave text-sm">Ranking por modalidade e etapa — atualizado em tempo real</p>
        </div>

        {/* Selector de campeonato */}
        {campeonatos.length > 1 && (
          <div className="mb-6">
            <select
              value={idcampeonato ?? ''}
              onChange={e => setIdcampeonato(parseInt(e.target.value, 10))}
              className="w-full bg-black/30 border border-white/20 text-white rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500"
            >
              {campeonatos.map(c => (
                <option key={c.idcampeonato} value={c.idcampeonato}>{c.descricao} — {c.temporada}</option>
              ))}
            </select>
          </div>
        )}

        {error && (
          <div className="mb-4 bg-red-900/40 border border-red-500/50 rounded-xl p-4 text-red-300 text-sm">
            {error}
          </div>
        )}

        {loading && (
          <div className="text-center py-16 text-compumaq-textoSuave">Carregando resultados...</div>
        )}

        {!loading && data && (
          <>
            {totalComResultados === 0 ? (
              <div className="bg-compumaq-pretoSuave/5 border border-white/10 rounded-2xl p-12 text-center">
                <div className="w-16 h-16 rounded-full bg-yellow-500/10 flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-yellow-500/50" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
                <p className="text-white font-semibold text-lg mb-2">Resultados serao publicados conforme as provas forem realizadas</p>
                <p className="text-compumaq-textoSuave text-sm">{campNome}</p>
              </div>
            ) : (
              <>
                {/* Tabs de modalidade */}
                {data.modalidades.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-4">
                    {data.modalidades.map(mod => (
                      <button
                        key={mod.idmodalidade}
                        onClick={() => handleSelectModalidade(mod.idmodalidade)}
                        className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all ${
                          tabModalidade === mod.idmodalidade
                            ? 'bg-yellow-500 text-compumaq-texto'
                            : 'bg-compumaq-pretoSuave/10 text-compumaq-textoSuave hover:bg-compumaq-pretoSuave/20 border border-white/10'
                        }`}
                      >
                        {mod.nome}
                      </button>
                    ))}
                  </div>
                )}

                {/* Filtro de etapa (dropdown) */}
                {modalidadeSel && etapasDisponiveis.length > 0 && (
                  <div className="mb-5">
                    <select
                      value={filtroEtapa === 'todas' ? 'todas' : (filtroEtapa ?? '')}
                      onChange={e => {
                        const val = e.target.value
                        handleFiltroEtapa(val === 'todas' ? 'todas' : parseInt(val, 10))
                      }}
                      className="bg-black/30 border border-white/20 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-compumaq-dourado min-w-[220px]"
                    >
                      {etapasOrdenadasDesc.map(et => (
                        <option key={et.idetapa} value={et.idetapa}>
                          {et.titulo_reduzido || `${et.numero_etapa}a Etapa`}
                        </option>
                      ))}
                      <option value="todas">Todas as Etapas (Ranking Geral)</option>
                    </select>
                  </div>
                )}

                {/* Tabela etapa especifica */}
                {filtroEtapa !== 'todas' && etapaSel && (
                  <div className="bg-compumaq-pretoSuave/5 border border-white/10 rounded-2xl overflow-hidden">
                    <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between">
                      <div>
                        <h3 className="text-white font-bold">{modalidadeSel?.nome}</h3>
                        <p className="text-compumaq-textoSuave text-xs mt-0.5">
                          {etapaSel.titulo || `Etapa ${etapaSel.numero_etapa}`} &bull; {etapaSel.resultados.length} resultado{etapaSel.resultados.length !== 1 ? 's' : ''}
                        </p>
                      </div>
                      <span className="text-xs text-compumaq-textoSuave bg-compumaq-pretoSuave/5 border border-white/10 px-2 py-1 rounded-lg">
                        {modalidadeSel?.grupo}
                      </span>
                    </div>

                    {etapaSel.resultados.length === 0 ? (
                      <div className="text-center py-10 text-compumaq-textoSuave text-sm">
                        Nenhum resultado lancado para esta etapa ainda.
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-white/10">
                              <th className="text-left px-5 py-3 text-compumaq-textoSuave font-medium text-xs uppercase tracking-wider w-10">#</th>
                              <th className="text-left px-5 py-3 text-compumaq-textoSuave font-medium text-xs uppercase tracking-wider">Atirador</th>
                              <th className="text-left px-4 py-3 text-compumaq-textoSuave font-medium text-xs uppercase tracking-wider hidden sm:table-cell">Clube</th>
                              {modalidadeSel?.grupo === 'Precisao' && (
                                <>
                                  <th className="text-right px-4 py-3 text-compumaq-textoSuave font-medium text-xs uppercase tracking-wider">Pts</th>
                                  <th className="text-right px-4 py-3 text-compumaq-textoSuave font-medium text-xs uppercase tracking-wider">X</th>
                                </>
                              )}
                              {modalidadeSel?.grupo === 'Silhueta' && (
                                <>
                                  <th className="text-right px-4 py-3 text-compumaq-textoSuave font-medium text-xs uppercase tracking-wider hidden md:table-cell">Carn.</th>
                                  <th className="text-right px-4 py-3 text-compumaq-textoSuave font-medium text-xs uppercase tracking-wider hidden md:table-cell">Peru</th>
                                  <th className="text-right px-4 py-3 text-compumaq-textoSuave font-medium text-xs uppercase tracking-wider hidden md:table-cell">Porco</th>
                                  <th className="text-right px-4 py-3 text-compumaq-textoSuave font-medium text-xs uppercase tracking-wider hidden md:table-cell">Galin.</th>
                                  <th className="text-right px-4 py-3 text-compumaq-textoSuave font-medium text-xs uppercase tracking-wider">Total</th>
                                </>
                              )}
                              {modalidadeSel?.grupo !== 'Precisao' && modalidadeSel?.grupo !== 'Silhueta' && (
                                <th className="text-right px-4 py-3 text-compumaq-textoSuave font-medium text-xs uppercase tracking-wider">Total</th>
                              )}
                            </tr>
                          </thead>
                          <tbody>
                            {etapaSel.resultados.map((r, idx) => (
                              <tr key={idx} className={`border-b border-white/5 last:border-0 ${idx === 0 ? 'bg-yellow-500/5' : idx === 1 ? 'bg-compumaq-pretoSuave/5' : idx === 2 ? 'bg-orange-700/5' : ''}`}>
                                <td className="px-5 py-3">
                                  {idx === 0 ? (
                                    <span className="text-yellow-400 font-black text-base">1</span>
                                  ) : idx === 1 ? (
                                    <span className="text-compumaq-textoSuave font-bold">2</span>
                                  ) : idx === 2 ? (
                                    <span className="text-orange-400 font-bold">3</span>
                                  ) : (
                                    <span className="text-compumaq-textoSuave">{idx + 1}</span>
                                  )}
                                </td>
                                <td className="px-5 py-3">
                                  <p className="text-white font-medium">{r.nome_atirador}</p>
                                  <p className="text-compumaq-textoSuave text-xs">{r.categoria}</p>
                                </td>
                                <td className="px-4 py-3 text-compumaq-textoSuave text-xs hidden sm:table-cell">{r.clube_nome}</td>
                                {modalidadeSel?.grupo === 'Precisao' && (
                                  <>
                                    <td className="px-4 py-3 text-right">
                                      <span className="text-green-400 font-bold">{r.total_pontos ?? r.total ?? '—'}</span>
                                    </td>
                                    <td className="px-4 py-3 text-right text-yellow-400 font-semibold">{r.qtd_x ?? '—'}</td>
                                  </>
                                )}
                                {modalidadeSel?.grupo === 'Silhueta' && (
                                  <>
                                    <td className="px-4 py-3 text-right text-compumaq-textoSuave hidden md:table-cell">{r.carneiro ?? '—'}</td>
                                    <td className="px-4 py-3 text-right text-compumaq-textoSuave hidden md:table-cell">{r.peru ?? '—'}</td>
                                    <td className="px-4 py-3 text-right text-compumaq-textoSuave hidden md:table-cell">{r.porco ?? '—'}</td>
                                    <td className="px-4 py-3 text-right text-compumaq-textoSuave hidden md:table-cell">{r.galinha ?? '—'}</td>
                                    <td className="px-4 py-3 text-right">
                                      <span className="text-green-400 font-bold">{r.total ?? '—'}</span>
                                    </td>
                                  </>
                                )}
                                {modalidadeSel?.grupo !== 'Precisao' && modalidadeSel?.grupo !== 'Silhueta' && (
                                  <td className="px-4 py-3 text-right">
                                    <span className="text-green-400 font-bold">{r.total ?? '—'}</span>
                                  </td>
                                )}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}

                {/* Ranking Geral (todas as etapas) */}
                {filtroEtapa === 'todas' && (
                  <div className="bg-compumaq-pretoSuave/5 border border-white/10 rounded-2xl overflow-hidden">
                    <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between">
                      <div>
                        <h3 className="text-white font-bold">{modalidadeSel?.nome}</h3>
                        <p className="text-compumaq-textoSuave text-xs mt-0.5">Ranking Geral — todas as etapas somadas</p>
                      </div>
                      <span className="text-xs text-compumaq-textoSuave bg-compumaq-pretoSuave/5 border border-white/10 px-2 py-1 rounded-lg">
                        {modalidadeSel?.grupo}
                      </span>
                    </div>

                    {loadingRanking && (
                      <div className="text-center py-10 text-compumaq-textoSuave text-sm">Carregando ranking geral...</div>
                    )}

                    {!loadingRanking && rankingGeral && (
                      rankingGeral.ranking.length === 0 ? (
                        <div className="text-center py-10 text-compumaq-textoSuave text-sm">
                          Nenhum resultado disponivel para ranking geral.
                        </div>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="border-b border-white/10">
                                <th className="text-left px-5 py-3 text-compumaq-textoSuave font-medium text-xs uppercase tracking-wider w-10">#</th>
                                <th className="text-left px-5 py-3 text-compumaq-textoSuave font-medium text-xs uppercase tracking-wider">Atirador</th>
                                <th className="text-left px-4 py-3 text-compumaq-textoSuave font-medium text-xs uppercase tracking-wider hidden sm:table-cell">Clube</th>
                                {rankingGeral.etapas_disponiveis.map(et => (
                                  <th key={et.idetapa} className="text-right px-4 py-3 text-compumaq-textoSuave font-medium text-xs uppercase tracking-wider hidden md:table-cell">
                                    {et.titulo_reduzido || `${et.numero_etapa}a Et.`}
                                  </th>
                                ))}
                                <th className="text-right px-4 py-3 text-yellow-400 font-bold text-xs uppercase tracking-wider">Total</th>
                              </tr>
                            </thead>
                            <tbody>
                              {rankingGeral.ranking.map((r, idx) => (
                                <tr key={r.idatirador} className={`border-b border-white/5 last:border-0 ${idx === 0 ? 'bg-yellow-500/5' : idx === 1 ? 'bg-compumaq-pretoSuave/5' : idx === 2 ? 'bg-orange-700/5' : ''}`}>
                                  <td className="px-5 py-3">
                                    {idx === 0 ? (
                                      <span className="text-yellow-400 font-black text-base">1</span>
                                    ) : idx === 1 ? (
                                      <span className="text-compumaq-textoSuave font-bold">2</span>
                                    ) : idx === 2 ? (
                                      <span className="text-orange-400 font-bold">3</span>
                                    ) : (
                                      <span className="text-compumaq-textoSuave">{idx + 1}</span>
                                    )}
                                  </td>
                                  <td className="px-5 py-3">
                                    <p className="text-white font-medium">{r.nome_atirador}</p>
                                  </td>
                                  <td className="px-4 py-3 text-compumaq-textoSuave text-xs hidden sm:table-cell">{r.clube_nome}</td>
                                  {rankingGeral.etapas_disponiveis.map(et => (
                                    <td key={et.idetapa} className="px-4 py-3 text-right text-compumaq-textoSuave hidden md:table-cell">
                                      {r.etapas[et.numero_etapa] != null && r.etapas[et.numero_etapa] > 0
                                        ? r.etapas[et.numero_etapa]
                                        : <span className="text-compumaq-textoSuave">—</span>}
                                    </td>
                                  ))}
                                  <td className="px-4 py-3 text-right">
                                    <span className="text-green-400 font-bold">{r.total_geral}</span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )
                    )}
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  )
}
